import JsBarcode from 'jsbarcode';
import { LABEL_PRINT_HEIGHT_MM } from './inventoryLabelGeometry';

export type BarcodeSymbology = 'CODE128' | 'CODE39';

/** Inset under the bottom line so glyphs stay off the physical edge. */
export const LABEL_PANEL_PAD_MM = 0.12;
/** Air above the código / peso row. */
export const LABEL_TOP_PAD_MM = 1;
/** Meta row (código + peso). */
export const LABEL_META_HEIGHT_MM = 1.55;
export const LABEL_META_TO_BAR_GAP_MM = 0.15;
/** Línea inferior: código proveedor + talla, pinned to the bottom of the strip. */
export const LABEL_BOTTOM_LINE_HEIGHT_MM = 1.35;
/** Clearance so bars never cut the glyphs under them. */
export const LABEL_BARCODE_TO_BOTTOM_GAP_MM = 0.5;

/**
 * One barcode height for every label. The bottom line is always reserved;
 * a piece with no talla and no código proveedor just leaves that band blank.
 */
export const LABEL_BARCODE_HEIGHT_MM =
  LABEL_PRINT_HEIGHT_MM -
  LABEL_TOP_PAD_MM -
  LABEL_META_HEIGHT_MM -
  LABEL_META_TO_BAR_GAP_MM -
  LABEL_BARCODE_TO_BOTTOM_GAP_MM -
  LABEL_BOTTOM_LINE_HEIGHT_MM -
  LABEL_PANEL_PAD_MM;

/** @deprecated Same slot as LABEL_BARCODE_HEIGHT_MM; kept so older imports still resolve. */
export const LABEL_BARCODE_WITH_BOTTOM_LINE_HEIGHT_MM = LABEL_BARCODE_HEIGHT_MM;

/** Zebra 203 dpi head ≈ 8 dots/mm; EPL and the print raster share this density. */
const LABEL_BARCODE_PRINT_DOTS_PER_MM = 8;
/** Screen preview only has to look right at CSS mm, so it renders a bit lighter. */
const LABEL_BARCODE_PREVIEW_PX_PER_MM = 7.2;

/** Bar module width; wider bars survive thermal printing better than the screen preview. */
export const LABEL_BARCODE_PREVIEW_MODULE_WIDTH = 1.2;
export const LABEL_BARCODE_PRINT_MODULE_WIDTH = 2;
export const LABEL_BARCODE_PRINT_MARGIN = 4;

/** Slot height reserved for the barcode. Always the same, bottom line or not. */
export function labelBarcodeHeightMm(_hasBottomLine: boolean): number {
  return LABEL_BARCODE_HEIGHT_MM;
}

/**
 * Bar height in generator units, derived from the slot height so the rendered
 * barcode fills its slot instead of being squashed by CSS (squashing narrows
 * the effective module height and hurts scan rates).
 */
export function labelBarcodePrintHeightDots(hasBottomLine: boolean): number {
  return Math.round(labelBarcodeHeightMm(hasBottomLine) * LABEL_BARCODE_PRINT_DOTS_PER_MM);
}

export function labelBarcodePreviewHeightPx(hasBottomLine: boolean): number {
  return Math.round(labelBarcodeHeightMm(hasBottomLine) * LABEL_BARCODE_PREVIEW_PX_PER_MM);
}

const BARCODE_ASPECT_RATIO = 'xMidYMid meet';

/**
 * Renders CODE128 (default) onto an SVG element. Value must match productCodeUnique for scanners.
 */
export function renderBarcodeSvg(
  svgEl: SVGSVGElement | null,
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): void {
  if (svgEl == null) return;
  // Clear before the blank check so a cleared value also clears the rendered barcode.
  while (svgEl.firstChild) {
    svgEl.removeChild(svgEl.firstChild);
  }
  if (value.trim() === '') return;
  try {
    JsBarcode(svgEl, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.85,
      height: options?.height ?? 28,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
    svgEl.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  } catch {
    /* invalid data for symbology — leave svg empty */
  }
}

/** Inline SVG string (preview / fallback). */
export function barcodeToSvgString(
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number },
): string {
  if (!value?.trim()) return '';
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? 0.75,
      height: options?.height ?? 26,
      displayValue: false,
      margin: 0,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return '';
  }
  svg.setAttribute('preserveAspectRatio', BARCODE_ASPECT_RATIO);
  return svg.outerHTML;
}

/**
 * Raster CODE128 PNG for Chrome → ZDesigner. Avoids SVG print blur on thermal.
 */
export function barcodeToPngDataUrl(
  value: string,
  options?: { format?: BarcodeSymbology; width?: number; height?: number; margin?: number },
): string {
  if (!value?.trim()) return '';
  const canvas = document.createElement('canvas');
  const quiet = options?.margin ?? LABEL_BARCODE_PRINT_MARGIN;
  try {
    JsBarcode(canvas, value, {
      format: options?.format ?? 'CODE128',
      width: options?.width ?? LABEL_BARCODE_PRINT_MODULE_WIDTH,
      height: options?.height ?? labelBarcodePrintHeightDots(false),
      displayValue: false,
      // Horizontal quiet zone only — vertical margin would inflate the PNG and
      // make CSS aspect-ratio overflow the flex slot (bars cutting the bottom line).
      marginTop: 0,
      marginBottom: 0,
      marginLeft: quiet,
      marginRight: quiet,
      background: '#ffffff',
      lineColor: '#000000',
    });
  } catch {
    return '';
  }
  return canvas.toDataURL('image/png');
}
